import { create } from 'zustand';
import { MasterPatient } from '@/types';
import { supabase } from '@/lib/supabase';
import { getCurrentHospitalId, requireHospitalIdForWrite } from '@/lib/hospitalContext';
import { trackChange } from '@/lib/utils/sync-tracker';
import { hospitalCacheKey } from '@/lib/utils/tenantCache';

interface MasterPatientsState {
  masterPatients: MasterPatient[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
  pendingUpdates: Set<string>;
  
  // Core operations
  fetchMasterPatients: () => Promise<void>;
  getMasterPatient: (id: string) => MasterPatient | undefined;
  getMasterPatientByContact: (contactNumber: string) => MasterPatient | undefined;
  
  // CRUD operations
  createMasterPatient: (patient: {
    name: string;
    age: string;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    location?: string;
    referralDoctor?: string;
    emergencyContact?: string;
    bloodGroup?: string;
    allergies?: string;
    medicalHistory?: string;
  }) => Promise<string>; // Returns master patient ID
  
  updateMasterPatient: (id: string, updates: Partial<MasterPatient>) => Promise<void>;
  deleteMasterPatient: (id: string) => Promise<void>;
  
  // Search operations
  searchMasterPatients: (query: string) => MasterPatient[];
  
  // Utility functions
  findOrCreateMasterPatient: (patient: {
    name: string;
    age: string;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    location?: string;
    referralDoctor?: string;
  }) => Promise<string>; // Returns master patient ID
  resetStore: () => void;
}

function isUniqueViolation(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return (
    error.code === '23505' ||
    /duplicate key|unique constraint/i.test(error.message ?? '')
  );
}

/** Next MP-YYYY-XXXX from visible rows + extra ids (e.g. failed insert attempts). */
async function resolveNextMasterPatientId(
  localPatients: MasterPatient[],
  extraIds: string[] = []
): Promise<string> {
  const year = new Date().getFullYear().toString();
  const prefix = `MP-${year}-`;

  const { data: rows } = await supabase
    .from('master_patients')
    .select('id')
    .like('id', `${prefix}%`);

  const dbIds = (rows || []).map((r) => r.id as string);
  const { generateMasterPatientId } = await import('../utils/patientId');
  return generateMasterPatientId(localPatients, [...dbIds, ...extraIds]);
}

async function createMasterPatientViaRpc(
  hospitalId: string,
  patient: {
    name: string;
    age: string;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    location?: string;
    referralDoctor?: string;
  }
): Promise<string | null> {
  const ageValue =
    typeof patient.age === 'number' ? String(patient.age) : String(patient.age);

  const { data, error } = await supabase.rpc('find_or_create_master_patient', {
    p_name: patient.name,
    p_age: ageValue,
    p_gender: patient.gender,
    p_contact_number: patient.contactNumber.trim(),
    p_location: patient.location ?? null,
    p_referral_doctor: patient.referralDoctor ?? null,
    p_hospital_id: hospitalId,
  });

  if (error) {
    if (error.code === 'PGRST202' || /Could not find the function/i.test(error.message ?? '')) {
      console.warn('find_or_create_master_patient RPC unavailable, using direct insert');
      return null;
    }
    throw error;
  }

  if (typeof data === 'string' && data.length > 0) {
    return data;
  }
  return null;
}

async function lookupMasterPatientByContact(
  hospitalId: string,
  contactNumber: string
): Promise<string | null> {
  const normalized = contactNumber.trim();
  if (!normalized) return null;

  const { data, error } = await supabase
    .from('master_patients')
    .select('id')
    .eq('hospital_id', hospitalId)
    .eq('contact_number', normalized)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.warn('Master patient lookup by contact:', error.message);
  }

  return data?.id ?? null;
}

const saveMasterPatientsCache = async (patients: MasterPatient[]) => {
  const hospitalId = await getCurrentHospitalId();
  if (!hospitalId) return;
  try {
    localStorage.setItem(
      hospitalCacheKey('masterPatientsCache', hospitalId),
      JSON.stringify({ data: patients, timestamp: Date.now() })
    );
  } catch (error) {
    console.warn('Failed to save master patients cache:', error);
  }
};

export const useMasterPatientsStore = create<MasterPatientsState>()((set, get) => ({
  masterPatients: [],
  isLoading: false,
  error: null,
  lastUpdated: 0,
  pendingUpdates: new Set(),

  resetStore: () => {
    set({
      masterPatients: [],
      isLoading: false,
      error: null,
      lastUpdated: 0,
      pendingUpdates: new Set(),
    });
  },

  fetchMasterPatients: async () => {
    set({ isLoading: true, error: null });
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        set({ masterPatients: [], isLoading: false, error: null });
        return;
      }

      console.log('Fetching master patients for hospital:', hospitalId);
      const { data, error } = await supabase
        .from('master_patients')
        .select('*')
        .eq('hospital_id', hospitalId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching master patients:', error);
        throw error;
      }

      // Map snake_case to camelCase
      const mappedPatients = (data || []).map(patient => ({
        id: patient.id,
        name: patient.name,
        age: patient.age,
        gender: patient.gender,
        contactNumber: patient.contact_number,
        location: patient.location,
        referralDoctor: patient.referral_doctor,
        emergencyContact: patient.emergency_contact,
        bloodGroup: patient.blood_group,
        allergies: patient.allergies,
        medicalHistory: patient.medical_history,
        createdAt: patient.created_at,
        updatedAt: patient.updated_at
      }));

      console.log('Fetched master patients:', mappedPatients);
      set({ masterPatients: mappedPatients, isLoading: false, lastUpdated: Date.now() });
      await saveMasterPatientsCache(mappedPatients);
    } catch (error) {
      console.error('Error fetching master patients:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  getMasterPatient: (id: string) => {
    return get().masterPatients.find(p => p.id === id);
  },

  getMasterPatientByContact: (contactNumber: string) => {
    return get().masterPatients.find(p => p.contactNumber === contactNumber);
  },

  createMasterPatient: async (patient) => {
    try {
      const hospitalId = await requireHospitalIdForWrite();

      const existingInMemory = get().getMasterPatientByContact(patient.contactNumber);
      if (existingInMemory) {
        return existingInMemory.id;
      }

      const existingInDb = await lookupMasterPatientByContact(
        hospitalId,
        patient.contactNumber
      );
      if (existingInDb) {
        return existingInDb;
      }

      const rpcId = await createMasterPatientViaRpc(hospitalId, patient);
      if (rpcId) {
        await get().fetchMasterPatients();
        return rpcId;
      }

      const ageForDb =
        typeof patient.age === 'number'
          ? patient.age
          : parseInt(String(patient.age).replace(/\D/g, ''), 10) || 0;

      const payload = {
        name: patient.name,
        age: ageForDb,
        gender: patient.gender,
        contact_number: patient.contactNumber.trim(),
        location: patient.location,
        referral_doctor: patient.referralDoctor,
        hospital_id: hospitalId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { bumpMasterPatientId } = await import('../utils/patientId');
      const triedIds: string[] = [];
      let masterPatientId = await resolveNextMasterPatientId(get().masterPatients, triedIds);

      for (let attempt = 0; attempt < 50; attempt++) {
        const { data, error } = await supabase
          .from('master_patients')
          .insert([{ id: masterPatientId, ...payload }])
          .select('id')
          .single();

        if (!error) {
          await get().fetchMasterPatients();
          return data.id;
        }

        if (isUniqueViolation(error)) {
          const again = await lookupMasterPatientByContact(
            hospitalId,
            patient.contactNumber
          );
          if (again) {
            return again;
          }
          triedIds.push(masterPatientId);
          masterPatientId = bumpMasterPatientId(masterPatientId);
          continue;
        }

        console.error('❌ Master patient creation failed:', error);
        throw error;
      }

      throw new Error('Could not allocate a unique master patient ID. Please try again.');
      
    } catch (error) {
      console.error('❌ FATAL ERROR in createMasterPatient:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      set({ error: (error as Error).message });
      console.log('=== END: Function failed ===');
      throw error;
    }
  },

  updateMasterPatient: async (id, updates) => {
    try {
      const currentPatient = get().masterPatients.find(p => p.id === id);
      if (!currentPatient) throw new Error('Master patient not found');

      // Create optimistic update
      const optimisticPatient: MasterPatient = {
        ...currentPatient,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update
      set(state => {
        const updated = state.masterPatients.map(p => p.id === id ? optimisticPatient : p);
        void saveMasterPatientsCache(updated);
        return { 
          masterPatients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, id])
        };
      });

      // Track change for incremental sync
      trackChange({
        id,
        table: 'master_patients',
        operation: 'update',
        data: {
          name: updates.name,
          age: updates.age,
          gender: updates.gender,
          contact_number: updates.contactNumber,
          location: updates.location,
          referral_doctor: updates.referralDoctor,
          emergency_contact: updates.emergencyContact,
          blood_group: updates.bloodGroup,
          allergies: updates.allergies,
          medical_history: updates.medicalHistory,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          name: currentPatient.name,
          age: currentPatient.age,
          gender: currentPatient.gender,
          contact_number: currentPatient.contactNumber,
          location: currentPatient.location,
          referral_doctor: currentPatient.referralDoctor,
          emergency_contact: currentPatient.emergencyContact,
          blood_group: currentPatient.bloodGroup,
          allergies: currentPatient.allergies,
          medical_history: currentPatient.medicalHistory,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync
      const { data, error } = await supabase
        .from('master_patients')
        .update({
          name: updates.name,
          age: updates.age,
          gender: updates.gender,
          contact_number: updates.contactNumber,
          location: updates.location,
          referral_doctor: updates.referralDoctor,
          emergency_contact: updates.emergencyContact,
          blood_group: updates.bloodGroup,
          allergies: updates.allergies,
          medical_history: updates.medicalHistory,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating master patient:', error);
        // Rollback optimistic update
        set(state => {
          const updated = state.masterPatients.map(p => p.id === id ? currentPatient : p);
          void saveMasterPatientsCache(updated);
          return { 
            masterPatients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(pid => pid !== id)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        location: data.location,
        referralDoctor: data.referral_doctor,
        emergencyContact: data.emergency_contact,
        bloodGroup: data.blood_group,
        allergies: data.allergies,
        medicalHistory: data.medical_history,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      set(state => {
        const updated = state.masterPatients.map(p => p.id === id ? mappedPatient : p);
        void saveMasterPatientsCache(updated);
        return { 
          masterPatients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(pid => pid !== id))
        };
      });
    } catch (error) {
      console.error('Error updating master patient:', error);
      set({ error: (error as Error).message });
    }
  },

  deleteMasterPatient: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('master_patients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => {
        const updated = state.masterPatients.filter(p => p.id !== id);
        void saveMasterPatientsCache(updated);
        return { masterPatients: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  searchMasterPatients: (query: string) => {
    const patients = get().masterPatients;
    if (!query.trim()) return patients;

    const searchTerm = query.toLowerCase().trim();
    return patients.filter(patient => 
      patient.name.toLowerCase().includes(searchTerm) ||
      patient.contactNumber.includes(searchTerm)
    );
  },

  findOrCreateMasterPatient: async (patient) => {
    try {
      const hospitalId = await requireHospitalIdForWrite();

      const existingPatient = get().getMasterPatientByContact(patient.contactNumber);
      if (existingPatient) {
        return existingPatient.id;
      }

      const existingInDb = await lookupMasterPatientByContact(
        hospitalId,
        patient.contactNumber
      );
      if (existingInDb) {
        return existingInDb;
      }

      return await get().createMasterPatient(patient);
    } catch (error) {
      console.error('Error in findOrCreateMasterPatient:', error);
      throw error;
    }
  }
})); 